import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_URL, getToken, BULAN , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { ABSENSI_STATUS, ABSENSI_LABEL } from "@/lib/constants";
import { MatrixBulanan, MatrixTahunan } from "@/components/AbsensiMatrix";

const THIS_YEAR = new Date().getFullYear();
const TAHUN_OPTS = Array.from({ length: 5 }, (_, i) => String(THIS_YEAR - i));

function StatusBadge({ status }) {
  const s = ABSENSI_STATUS.find((x) => x.v === status);
  if (!s) return <span className="text-slate-400 italic text-xs">{ABSENSI_LABEL[status] || "—"}</span>;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}>{s.label}</span>;
}

export function AbsensiGuru() {
  const [pend, setPend] = useState([]);
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [marks, setMarks] = useState({});
  const [busy, setBusy] = useState(false);

  // Riwayat & filter
  const [history, setHistory] = useState([]);           // absensi milik guru (default)
  const [rekapAbs, setRekapAbs] = useState([]);         // absensi seluruh siswa untuk mode matrix
  const [siswaAll, setSiswaAll] = useState([]);
  const [fSiswa, setFSiswa] = useState("");             // nama siswa (search text)
  const [fBulan, setFBulan] = useState("");
  const [fTahun, setFTahun] = useState("");
  const [fSiswaStatus, setFSiswaStatus] = useState("");
  const [applied, setApplied] = useState({ siswa: "", bulan: "", tahun: "", siswa_status: "" });

  const loadMark = useCallback(async () => {
    const [{ data: p }, { data: a }] = await Promise.all([api.get("/pendampingan"), api.get("/absensi", { params: { tanggal } })]);
    setPend(p);
    const m = {}; a.forEach(x => { m[x.siswa_id] = x.status; }); setMarks(m);
  }, [tanggal]);
  useEffect(() => { loadMark(); }, [loadMark]);

  // Load siswa list (for matrix rows)
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/siswa"); setSiswaAll(data || []); } catch { /* ignore */ }
    })();
  }, []);

  // Riwayat: milik guru (default). Also load rekap (scope=all) when bulan/tahun aktif.
  const loadHistory = useCallback(async () => {
    const params = {};
    if (applied.bulan && applied.tahun) { params.bulan = applied.bulan; params.tahun = applied.tahun; }
    try {
      const { data } = await api.get("/absensi", { params });
      setHistory(data);
    } catch (e) { console.error(e); }
  }, [applied]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Rekap semua siswa (untuk matrix)
  useEffect(() => {
    if (!applied.bulan && !applied.tahun) { setRekapAbs([]); return; }
    (async () => {
      try {
        const params = { scope: "all" };
        if (applied.bulan) params.bulan = applied.bulan;
        if (applied.tahun) params.tahun = applied.tahun;
        const { data } = await api.get("/absensi", { params });
        setRekapAbs(data || []);
      } catch (e) { console.error(e); }
    })();
  }, [applied.bulan, applied.tahun]);

  const save = async () => {
    setBusy(true);
    try {
      for (const p of pend) {
        await api.post("/absensi", { siswa_id: p.siswa_id, tanggal, status: marks[p.siswa_id] || "hadir" });
      }
      toast.success("Absensi tersimpan");
      loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const apply = () => setApplied({ siswa: fSiswa, bulan: fBulan, tahun: fTahun, siswa_status: fSiswaStatus });
  const reset = () => {
    setFSiswa(""); setFBulan(""); setFTahun(""); setFSiswaStatus("");
    setApplied({ siswa: "", bulan: "", tahun: "", siswa_status: "" });
  };

  const inRekap = applied.bulan || applied.tahun;

  const historyFiltered = useMemo(() => history.filter((h) => {
    if (applied.siswa && !(h.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    return true;
  }), [history, applied.siswa]);

  const downloadPdf = () => {
    const params = new URLSearchParams();
    if (applied.bulan) params.set("bulan", applied.bulan);
    if (applied.tahun) params.set("tahun", applied.tahun);
    const url = `${API_URL}/export/absensi?${params.toString()}`;
    const t = getToken();
    fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: "include" })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `rekap-absensi.pdf`;
        a.click(); URL.revokeObjectURL(u);
      });
  };

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Absensi Siswa Pendampingan" />

      {/* Section: Input Absensi Hari Ini */}
      <section className="pp-card p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div><label className="pp-label">Tanggal</label><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className="pp-input" data-testid="absensi-tgl" /></div>
        <button onClick={save} disabled={busy || pend.length === 0} className="pp-btn-primary" data-testid="save-absensi">{busy ? "Menyimpan…" : "Simpan Absensi"}</button>
      </section>
      <section className="pp-card overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full text-sm" data-testid="absensi-tbl">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3 min-w-[200px]">Nama Siswa</th>
              <th className="text-left px-5 py-3">Pilih Status</th>
              <th className="text-left px-5 py-3 w-40">Status</th>
            </tr></thead>
            <tbody>
              {pend.length === 0 && <tr><td colSpan={3} className="text-center py-10 text-slate-400 italic">Belum ada siswa pendampingan.</td></tr>}
              {pend.map((p) => (
                <tr key={p.id} className="pp-row">
                  <td className="px-5 py-3 font-medium">{p.nama_siswa}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {ABSENSI_STATUS.map((s) => (
                        <button
                          key={s.v}
                          type="button"
                          onClick={() => setMarks({ ...marks, [p.siswa_id]: s.v })}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${marks[p.siswa_id] === s.v ? s.cls : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                          data-testid={`m-${s.v}-${p.siswa_id}`}
                        >{s.label}</button>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3">{marks[p.siswa_id] ? <StatusBadge status={marks[p.siswa_id]} /> : <span className="text-slate-400 italic text-xs">Belum diisi</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section: Riwayat / Rekap + Filter */}
      <div className="mb-3 flex items-end justify-between gap-3 flex-wrap">
        <h2 className="text-xl font-bold text-slate-900">{inRekap ? "Rekap Absensi Siswa" : "Riwayat Absensi (Diinput oleh Saya)"}</h2>
        {!inRekap && (
          <button onClick={downloadPdf} className="pp-btn-secondary" data-testid="dl-absensi-pdf-guru">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        )}
      </div>
      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input
            type="text"
            className="pp-input"
            placeholder="Cari nama siswa"
            value={fSiswa}
            onChange={(e) => setFSiswa(e.target.value)}
            data-testid="filter-siswa-absensi"
          />
          <select className="pp-input" value={fBulan} onChange={(e) => setFBulan(e.target.value)} data-testid="filter-bulan-absensi">
            <option value="">Semua bulan</option>
            {BULAN.map(b => <option key={b.v} value={b.v}>{b.label}</option>)}
          </select>
          <select className="pp-input" value={fSiswaStatus} onChange={(e) => setFSiswaStatus(e.target.value)} data-testid="filter-siswa-status-absensi">
            <option value="">Seluruh Siswa</option>
            <option value="aktif">Siswa Aktif</option>
            <option value="tidak_aktif">Siswa Tidak Aktif</option>
          </select>
          <select className="pp-input" value={fTahun} onChange={(e) => setFTahun(e.target.value)} data-testid="filter-tahun-absensi">
            <option value="">Semua tahun</option>
            {TAHUN_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex gap-2">
            <button onClick={apply} className="pp-btn-primary flex-1" data-testid="apply-filter-absensi">Terapkan</button>
            <button onClick={reset} className="pp-btn-secondary flex-1" data-testid="reset-filter-absensi">Reset</button>
          </div>
        </div>
      </section>

      {applied.bulan ? (
        <MatrixBulanan
          absensi={rekapAbs}
          siswaAll={siswaAll}
          bulan={applied.bulan}
          tahun={applied.tahun || String(new Date().getFullYear())}
          siswaStatus={applied.siswa_status}
          siswaSearch={applied.siswa}
        />
      ) : applied.tahun ? (
        <MatrixTahunan
          absensi={rekapAbs}
          siswaAll={siswaAll}
          tahun={applied.tahun}
          siswaStatus={applied.siswa_status}
          siswaSearch={applied.siswa}
        />
      ) : (
        <section className="pp-card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th>
              <th className="text-left px-5 py-3">Nama Siswa</th>
              <th className="text-left px-5 py-3">Status</th>
            </tr></thead>
            <tbody>
              {historyFiltered.length === 0 && <tr><td colSpan={3} className="text-center py-10 text-slate-400 italic">Belum ada riwayat.</td></tr>}
              {historyFiltered.map((a) => (
                <tr key={a.id} className="pp-row">
                  <td className="px-5 py-3 font-mono text-xs">{formatTanggal(a.tanggal)}</td>
                  <td className="px-5 py-3 font-medium">{a.nama_siswa}</td>
                  <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Layout>
  );
}
